import type { OnboardingDiscipline, OnboardingTemplate } from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, MoreHorizontal, Plus } from "lucide-react";
import { useState } from "react";
import { useLocation, useSearch } from "wouter";
import { BuddyPoolTab } from "@/components/onboardings/buddy-pool-tab";
import { StartOnboardingDialog } from "@/components/onboardings/start-onboarding-dialog";
import { StatusPill } from "@/components/onboardings/status-pill";
import { PageHeader } from "@/components/page-header";
import { CreateTemplateModal } from "@/components/template/create-template-modal";
import { Button } from "@/components/ui/button";
import { type CanonicalColumn, CanonicalTable } from "@/components/ui/canonical-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { DISCIPLINE_LABELS } from "@/lib/discipline-labels";
import { orpc, rpcClient } from "@/lib/rpc";

function formatStartDate(iso: string): string {
  // BR-formatted, locked to São Paulo to stay consistent with backend math.
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1, 12, 0, 0));
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dt);
}

type TemplateListItem = OnboardingTemplate & { activeOnboardingsCount?: number };

export default function OnboardingsPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isSuperAdmin, isManager, hasOnboardingTemplateAccess, hasPermission } = useAuth();
  const canManage =
    isSuperAdmin || isManager || hasPermission("onboarding") || hasOnboardingTemplateAccess;
  // Buddy pool management is gated by its own granular permission (admin bypasses
  // via hasPermission). Independent of `canManage` so it can be granted alone.
  const canManageBuddies = hasPermission("buddy_pool");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<TemplateListItem | null>(null);

  const initialTabParam = new URLSearchParams(search).get("tab");
  const initialTab: "active" | "templates" | "buddies" =
    initialTabParam === "templates"
      ? "templates"
      : initialTabParam === "buddies"
        ? "buddies"
        : "active";
  const [tab, setTab] = useState<"active" | "templates" | "buddies">(initialTab);

  const list = useQuery(orpc.onboardings.listActive.queryOptions());
  const drafts = useQuery({
    ...orpc.onboardings.listDrafts.queryOptions(),
    enabled: canManage,
  });
  const templatesQuery = useQuery({
    ...orpc.onboardingTemplates.list.queryOptions(),
    enabled: canManage,
  });

  const rows = list.data ?? [];
  const draftRows = drafts.data ?? [];
  const templates: TemplateListItem[] = (templatesQuery.data ?? []) as TemplateListItem[];

  const deleteTemplate = useMutation({
    mutationFn: (id: string) => rpcClient.onboardingTemplates.remove({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardingTemplates.list.queryOptions().queryKey,
      });
      toast({ title: "Template excluído" });
    },
    onError: (err: any) =>
      toast({
        title: "Não foi possível excluir o template",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      }),
  });

  function handleDeleteTemplate(t: TemplateListItem) {
    if (!window.confirm(`Excluir o template "${t.name}"? Esta ação não pode ser desfeita.`)) return;
    deleteTemplate.mutate(t.id);
  }

  const discardDraft = useMutation({
    mutationFn: (id: string) => rpcClient.onboardings.discardDraft({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listDrafts.queryOptions().queryKey,
      });
      toast({ title: "Rascunho descartado" });
    },
    onError: (err: any) =>
      toast({
        title: "Erro ao descartar",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      }),
  });

  function handleDiscardDraft(id: string, hireFullName: string) {
    if (!window.confirm(`Descartar rascunho de ${hireFullName}? Esta ação não pode ser desfeita.`))
      return;
    discardDraft.mutate(id);
  }

  return (
    <div className="p-6">
      <PageHeader title="Onboarding">
        {canManage && tab === "active" && (
          <Button onClick={() => setDialogOpen(true)} data-testid="button-start-onboarding">
            <Plus className="mr-2 h-4 w-4" /> Iniciar onboarding
          </Button>
        )}
        {canManage && tab === "templates" && (
          <Button onClick={() => setCreateTemplateOpen(true)} data-testid="button-new-template">
            + Novo template
          </Button>
        )}
      </PageHeader>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as "active" | "templates" | "buddies")}
        className="mt-4"
      >
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">
            Ativos
          </TabsTrigger>
          {canManage && (
            <TabsTrigger value="templates" data-testid="tab-templates">
              Templates
            </TabsTrigger>
          )}
          {canManageBuddies && (
            <TabsTrigger value="buddies" data-testid="tab-buddies">
              Buddies
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="active">
          {canManage && draftRows.length > 0 && (
            <Card
              className="mt-6 border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-2))]"
              data-testid="drafts-banner"
            >
              <CardContent className="py-4">
                <p className="text-sm font-medium">
                  Você tem {draftRows.length} rascunho{draftRows.length === 1 ? "" : "s"}. Retomar
                  ou descartar?
                </p>
                <ul className="mt-3 space-y-2">
                  {draftRows.map((d) => (
                    <li
                      key={d.id}
                      className="flex items-center justify-between gap-3 rounded border bg-background p-3"
                      data-testid={`draft-row-${d.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{d.hireFullName}</p>
                        <p className="text-xs text-muted-foreground">
                          {DISCIPLINE_LABELS[d.discipline as OnboardingDiscipline] ?? d.discipline}
                          {" · "}
                          {d.template?.name ?? "—"}
                          {" · Início "}
                          {formatStartDate(d.startDate)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/onboardings/${d.id}/customize`)}
                          data-testid={`button-resume-draft-${d.id}`}
                        >
                          Retomar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDiscardDraft(d.id, d.hireFullName)}
                          disabled={discardDraft.isPending}
                          data-testid={`button-discard-draft-${d.id}`}
                        >
                          Descartar
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <div className="mt-6">
            {!list.isLoading && rows.length === 0 ? (
              <div className="flex flex-col items-center gap-4 rounded-[9px] border border-[hsl(var(--card-line))] bg-[hsl(var(--paper-white))] py-12 text-center">
                <p className="text-base font-medium">
                  {canManage
                    ? "Nenhum onboarding ativo ainda"
                    : "Nenhum onboarding ativo como buddy"}
                </p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {canManage
                    ? "Crie o primeiro onboarding ativo para acompanhar um novo colaborador nos primeiros 60 dias."
                    : "Quando você for selecionado como buddy de um novo colaborador, o onboarding aparecerá aqui."}
                </p>
                {canManage && (
                  <Button
                    onClick={() => setDialogOpen(true)}
                    data-testid="button-start-onboarding-empty"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Iniciar onboarding
                  </Button>
                )}
              </div>
            ) : (
              <CanonicalTable<(typeof rows)[number]>
                title="Onboardings ativos"
                count={rows.length}
                isLoading={list.isLoading}
                data={rows}
                getRowId={(o) => o.id}
                onRowClick={(o) => navigate(`/onboardings/${o.id}`)}
                testIdPrefix="row-onboarding"
                columns={
                  [
                    {
                      key: "colaborador",
                      header: "Colaborador",
                      cell: (o) => (
                        <div>
                          <div className="font-semibold leading-tight">{o.hireFullName}</div>
                          <div className="text-[11.5px] text-[hsl(var(--ink-muted))]">
                            {o.hireEmail}
                          </div>
                          <div className="text-[11.5px] text-[hsl(var(--ink-muted))]">
                            Início: {formatStartDate(o.startDate)}
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: "disciplina",
                      header: "Disciplina",
                      cell: (o) => (
                        <div>
                          <div>
                            {DISCIPLINE_LABELS[o.discipline as OnboardingDiscipline] ??
                              o.discipline}
                          </div>
                          <div className="text-[11.5px] text-[hsl(var(--ink-muted))]">
                            {o.level}
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: "template",
                      header: "Template",
                      cell: (o) => o.template?.name ?? "—",
                    },
                    {
                      key: "dia",
                      header: "Dia",
                      cell: (o) => (
                        <span className="tabular-nums">
                          <span className="font-semibold">{o.currentDay}</span>
                          <span className="text-[hsl(var(--ink-muted))]">/60</span>
                        </span>
                      ),
                    },
                    {
                      key: "status",
                      header: "Status",
                      cell: (o) => <StatusPill value={o.statusPill} />,
                    },
                    {
                      key: "buddy",
                      header: "Buddy",
                      cell: (o) => o.buddy?.fullName ?? "—",
                    },
                    {
                      key: "gestor",
                      header: "Gestor direto",
                      cell: (o) => o.directManager?.fullName ?? "—",
                    },
                    {
                      key: "actions",
                      header: "",
                      stickyRight: true,
                      cell: (o) => (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/onboardings/${o.id}`);
                          }}
                          aria-label={`Abrir onboarding de ${o.hireFullName}`}
                          className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-[hsl(var(--ink-muted))] transition-colors hover:bg-[hsl(var(--sand-step))] hover:text-[hsl(var(--ink-primary))]"
                          data-testid={`link-view-${o.id}`}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      ),
                    },
                  ] satisfies CanonicalColumn<(typeof rows)[number]>[]
                }
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <p className="mt-2 text-sm text-muted-foreground">
            Clique em um template para abrir e editar.
          </p>

          {templatesQuery.isLoading && (
            <p className="mt-6 text-sm text-muted-foreground">Carregando templates…</p>
          )}

          {templatesQuery.isError && (
            <p className="mt-6 text-sm text-destructive">Não foi possível carregar os templates.</p>
          )}

          {!templatesQuery.isLoading && templates.length === 0 && (
            <p className="mt-6 text-sm text-muted-foreground">
              Nenhum template ainda. Crie o primeiro para começar.
            </p>
          )}

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((t) => (
              <Card
                key={t.id}
                className="h-full cursor-pointer transition-colors hover:bg-[hsl(var(--sand-step))]"
                onClick={() => navigate(`/onboarding-templates/${t.id}/canvas`)}
                data-testid={`card-template-${t.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base">{t.name}</CardTitle>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span
                          className={
                            t.state === "draft"
                              ? "text-[13px] font-semibold text-[hsl(var(--ink-muted))]"
                              : "text-[13px] font-semibold text-[hsl(var(--forest-pulse))]"
                          }
                          data-testid={`status-template-${t.state}`}
                        >
                          {t.state === "draft" ? "Rascunho" : "Publicado"}
                        </span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="-mr-1 -mt-1 h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Mais ações"
                          data-testid={`button-template-actions-${t.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem
                          onSelect={() => setDuplicateSource(t)}
                          data-testid={`button-duplicate-template-${t.id}`}
                        >
                          Duplicar template
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => handleDeleteTemplate(t)}
                          className="text-destructive focus:text-destructive"
                          data-testid={`button-delete-template-${t.id}`}
                        >
                          Excluir template
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  <p>
                    Última atualização:{" "}
                    <span className="text-foreground font-medium">
                      {t.updatedAt
                        ? new Date(t.updatedAt).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </span>
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="buddies">{canManageBuddies && <BuddyPoolTab />}</TabsContent>
      </Tabs>

      {canManage && (
        <>
          <StartOnboardingDialog open={dialogOpen} onOpenChange={setDialogOpen} />
          <CreateTemplateModal
            open={createTemplateOpen}
            onOpenChange={setCreateTemplateOpen}
            mode="create"
            onCreated={(created) => navigate(`/onboarding-templates/${created.id}/canvas`)}
          />
          <CreateTemplateModal
            open={!!duplicateSource}
            onOpenChange={(open) => {
              if (!open) setDuplicateSource(null);
            }}
            mode="duplicate"
            template={duplicateSource}
            onCreated={(created) => {
              setDuplicateSource(null);
              navigate(`/onboarding-templates/${created.id}/canvas`);
            }}
          />
        </>
      )}
    </div>
  );
}
